import { useEffect, useMemo } from "react";
import type { Project } from "@/types";
import type { Plot } from "@/hooks/usePlots";
import type { ProcurementPackage } from "@/hooks/useProcurementPackages";
import type { Section } from "@/hooks/useSections";
import type { AllLocationNode } from "@/hooks/useAllLocations";
import { useTaskFilterStore } from "@/stores/useTaskFilterStore";

type OptionWithNames = {
  value: string;
  label: string;
};

/**
 * Owns all of the cascading-filter logic for the task board:
 *
 *  - Narrows the valid set of plots / procurement packages / sections so each
 *    dropdown only lists options that are reachable from the parent filter
 *    (e.g. hide plots for un-selected projects).
 *  - Builds the "CODE|||NAME" composite keys the backend uses to match PPs
 *    and sections case-insensitively, along with name-maps that translate
 *    keys back to the original names for the GraphQL query.
 *  - Auto-deselects child filter values that fall out of scope when a parent
 *    filter narrows. Each effect is idempotent (only fires when the pruned
 *    length differs from the current length).
 *
 * Selected-but-out-of-scope values are NOT auto-cleared — they stay
 * remembered in the store and reactivate when the parent filter changes
 * back. `useServerFilterParams` intersects with the key-sets returned here
 * so the backend never receives a contradictory filter combination.
 */
export function useCascadingFilterScopes({
  projects,
  plots,
  procurementPackages,
  sections,
  locations,
  myProjectIdSet,
  myProjectRolesLoading,
  seesAllProjects,
}: {
  projects: Project[];
  plots: Plot[];
  procurementPackages: ProcurementPackage[];
  sections: Section[];
  locations: AllLocationNode[];
  myProjectIdSet: Set<string>;
  myProjectRolesLoading: boolean;
  /**
   * True when the viewer's server-side visibility is org-wide (ADMIN /
   * INTERNAL+ — see `compute_visible_project_ids`). Their access is not backed
   * by ProjectRole rows, so `myProjectIdSet` under-reports what they may see.
   */
  seesAllProjects: boolean;
}) {
  const {
    onlyMyProjects,
    projectFilter,
    plotFilter,
    procurementPackageFilter,
    workSectionFilter,
    setProjectFilter,
    setPlotFilter,
    setProcurementPackageFilter,
    setWorkSectionFilter,
    locationFilter,
    setLocationFilter,
  } = useTaskFilterStore();

  // Project scope: when "Vain omat hankkeet" is on, narrow to projects
  // where the current user has a role. Otherwise all projects.
  //
  // ADMIN / INTERNAL+ are exempt: the backend already returns every project to
  // them, and their visibility comes from the access level rather than from
  // ProjectRole rows, so intersecting with `myProjectIdSet` would disable every
  // hanke option for an admin who happens to hold no project role.
  const validProjectIdSet = useMemo(() => {
    if (!onlyMyProjects || seesAllProjects) {
      return new Set(projects.map((p) => p.id));
    }
    return new Set(
      projects.map((p) => p.id).filter((id) => myProjectIdSet.has(id)),
    );
  }, [projects, onlyMyProjects, seesAllProjects, myProjectIdSet]);

  // Plots reachable from the current project scope.
  const validPlots = useMemo(() => {
    if (projectFilter.length > 0) {
      const projectIds = new Set(projectFilter);
      return plots.filter((p) => projectIds.has(p.projectId));
    }
    if (onlyMyProjects) {
      return plots.filter((p) => validProjectIdSet.has(p.projectId));
    }
    return plots;
  }, [plots, projectFilter, onlyMyProjects, validProjectIdSet]);

  // Procurement packages reachable from the current plot scope.
  const validProcurementPackages = useMemo(() => {
    if (plotFilter.length > 0) {
      const plotIds = new Set(plotFilter);
      return procurementPackages.filter(
        (pp) => pp.plotId != null && plotIds.has(pp.plotId),
      );
    }
    if (projectFilter.length > 0 || onlyMyProjects) {
      const validPlotIds = new Set(validPlots.map((p) => p.id));
      return procurementPackages.filter(
        (pp) => pp.plotId != null && validPlotIds.has(pp.plotId),
      );
    }
    return procurementPackages;
  }, [procurementPackages, plotFilter, projectFilter, onlyMyProjects, validPlots]);

  // Sections reachable from the current PP scope. procurementPackageFilter
  // holds composite keys "CODE|||NAME"; resolve them to PP IDs using the same
  // normalization the ppOptions builder uses.
  const validSections = useMemo(() => {
    if (procurementPackageFilter.length > 0) {
      const selectedKeys = new Set(procurementPackageFilter);
      const ppIds = new Set(
        validProcurementPackages
          .filter((pp) => {
            const key = `${pp.code.toUpperCase()}|||${(pp.name || "").toUpperCase()}`;
            return selectedKeys.has(key);
          })
          .map((pp) => pp.id),
      );
      return sections.filter(
        (s) => s.procurementPackageId != null && ppIds.has(s.procurementPackageId),
      );
    }
    if (projectFilter.length > 0 || plotFilter.length > 0 || onlyMyProjects) {
      const validPPIds = new Set(validProcurementPackages.map((pp) => pp.id));
      return sections.filter(
        (s) => s.procurementPackageId != null && validPPIds.has(s.procurementPackageId),
      );
    }
    return sections;
  }, [sections, procurementPackageFilter, projectFilter, plotFilter, onlyMyProjects, validProcurementPackages]);

  // Locations reachable from the current project/plot scope.
  const validLocations = useMemo(() => {
    if (plotFilter.length > 0) {
      const plotIds = new Set(plotFilter);
      return locations.filter((l) => l.plotId != null && plotIds.has(l.plotId));
    }
    if (projectFilter.length > 0) {
      const projectIds = new Set(projectFilter);
      return locations.filter((l) => projectIds.has(l.projectId));
    }
    if (onlyMyProjects) {
      return locations.filter((l) => validProjectIdSet.has(l.projectId));
    }
    return locations;
  }, [locations, plotFilter, projectFilter, onlyMyProjects, validProjectIdSet]);

  const validLocationIdSet = useMemo(
    () => new Set(validLocations.map((l) => l.id)),
    [validLocations],
  );

  const validPlotIdSet = useMemo(
    () => new Set(validPlots.map((p) => p.id)),
    [validPlots],
  );
  const validPpKeySet = useMemo(
    () =>
      new Set(
        validProcurementPackages.map(
          (pp) => `${pp.code.toUpperCase()}|||${(pp.name || "").toUpperCase()}`,
        ),
      ),
    [validProcurementPackages],
  );
  const validSectionKeySet = useMemo(
    () =>
      new Set(
        validSections.map(
          (s) => `${(s.code || "").toUpperCase()}|||${(s.name || "").toUpperCase()}`,
        ),
      ),
    [validSections],
  );

  // Auto-prune: when "Vain omat hankkeet" is on and the project-roles query
  // has returned, drop any projectFilter entries no longer in scope. Skipped
  // while loading so a stale-but-valid projectFilter isn't cleared before
  // membership data arrives.
  useEffect(() => {
    if (!onlyMyProjects || myProjectRolesLoading) return;
    if (projectFilter.length === 0) return;
    const pruned = projectFilter.filter((id) => validProjectIdSet.has(id));
    if (pruned.length !== projectFilter.length) setProjectFilter(pruned);
  }, [validProjectIdSet, projectFilter, onlyMyProjects, myProjectRolesLoading, setProjectFilter]);

  useEffect(() => {
    if (plotFilter.length === 0 || projectFilter.length === 0) return;
    const pruned = plotFilter.filter((id) => validPlotIdSet.has(id));
    if (pruned.length !== plotFilter.length) setPlotFilter(pruned);
  }, [validPlotIdSet, plotFilter, projectFilter, setPlotFilter]);

  useEffect(() => {
    if (procurementPackageFilter.length === 0) return;
    if (projectFilter.length === 0 && plotFilter.length === 0) return;
    const pruned = procurementPackageFilter.filter((k) => validPpKeySet.has(k));
    if (pruned.length !== procurementPackageFilter.length) {
      setProcurementPackageFilter(pruned);
    }
  }, [validPpKeySet, procurementPackageFilter, projectFilter, plotFilter, setProcurementPackageFilter]);

  useEffect(() => {
    if (workSectionFilter.length === 0) return;
    if (
      projectFilter.length === 0 &&
      plotFilter.length === 0 &&
      procurementPackageFilter.length === 0
    )
      return;
    const pruned = workSectionFilter.filter((k) => validSectionKeySet.has(k));
    if (pruned.length !== workSectionFilter.length) {
      setWorkSectionFilter(pruned);
    }
  }, [validSectionKeySet, workSectionFilter, projectFilter, plotFilter, procurementPackageFilter, setWorkSectionFilter]);

  useEffect(() => {
    if (locationFilter.length === 0) return;
    if (projectFilter.length === 0 && plotFilter.length === 0 && !onlyMyProjects)
      return;
    const pruned = locationFilter.filter((id) => validLocationIdSet.has(id));
    if (pruned.length !== locationFilter.length) setLocationFilter(pruned);
  }, [validLocationIdSet, locationFilter, projectFilter, plotFilter, onlyMyProjects, setLocationFilter]);

  // Procurement package dropdown options: combine duplicates by CODE + Name
  // (case-insensitive). The nameMap retains the original (non-uppercased)
  // names so serverFilterParams can pass them verbatim to the backend.
  const { ppOptions, ppNameMap } = useMemo(() => {
    const grouped = new Map<
      string,
      { value: string; label: string; originalNames: string[] }
    >();

    procurementPackages.forEach((pp) => {
      const code = pp.code.toUpperCase();
      const name = (pp.name || "").toUpperCase();
      const key = `${code}|||${name}`;
      const label = pp.name ? `${code} ${name}` : code;

      if (!grouped.has(key)) {
        grouped.set(key, { value: key, label, originalNames: [pp.name || ""] });
      } else {
        grouped.get(key)!.originalNames.push(pp.name || "");
      }
    });

    const options: OptionWithNames[] = Array.from(grouped.values()).map(
      ({ value, label }) => ({ value, label }),
    );
    const nameMap = new Map<string, string[]>();
    grouped.forEach((group, key) => {
      nameMap.set(key, group.originalNames);
    });

    return { ppOptions: options, ppNameMap: nameMap };
  }, [procurementPackages]);

  // Work section dropdown options: same grouping as PPs.
  const { sectionOptions, sectionNameMap } = useMemo(() => {
    const grouped = new Map<
      string,
      { value: string; label: string; originalNames: string[] }
    >();

    sections.forEach((s) => {
      const code = (s.code || "").toUpperCase();
      const name = (s.name || "").toUpperCase();
      const key = `${code}|||${name}`;
      const label = s.code ? `${code} ${name}` : name;

      if (!grouped.has(key)) {
        grouped.set(key, { value: key, label, originalNames: [s.name || ""] });
      } else {
        grouped.get(key)!.originalNames.push(s.name || "");
      }
    });

    const options: OptionWithNames[] = Array.from(grouped.values()).map(
      ({ value, label }) => ({ value, label }),
    );
    const nameMap = new Map<string, string[]>();
    grouped.forEach((group, key) => {
      nameMap.set(key, group.originalNames);
    });

    return { sectionOptions: options, sectionNameMap: nameMap };
  }, [sections]);

  const locationOptions = useMemo<OptionWithNames[]>(() => {
    return locations.map((l) => ({
      value: l.id,
      label: l.hierarchyPath.length > 0 ? l.hierarchyPath.join(" › ") : l.name,
    }));
  }, [locations]);

  return {
    validProjectIdSet,
    validPlots,
    validPlotIdSet,
    validProcurementPackages,
    validPpKeySet,
    validSections,
    validSectionKeySet,
    ppOptions,
    ppNameMap,
    sectionOptions,
    sectionNameMap,
    validLocations,
    validLocationIdSet,
    locationOptions,
  };
}
